import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  DEFAULT_BRAND,
  FALLBACK_INSTANCE,
  fetchInstance,
  rgbTriple,
  type Brand,
  type InstanceInfo,
} from './api/instance';

/**
 * The instance name and look, fetched once and shared.
 *
 * A context rather than a fetch per component: the sidebar, the login card
 * and the dashboard subtitle all want it, and three fetches of a constant
 * is three chances for them to disagree.
 *
 * It also dresses the document — the tab title, the theme colour, the
 * favicon and the four colour variables the stylesheet is built on —
 * because those live outside React and the brand is what they should say.
 */

const InstanceContext = createContext<InstanceInfo>({
  name: FALLBACK_INSTANCE,
  brand: DEFAULT_BRAND,
});

export function useInstanceName(): string {
  return useContext(InstanceContext).name;
}

export function useBrand(): Brand {
  return useContext(InstanceContext).brand;
}

/**
 * Write the brand onto the document.
 *
 * **Four variables carry the whole palette.** `--color-accent` and
 * `--color-accent-secondary` (and the gradient built from them) are read by
 * the components; `--color-accent-rgb` is what every translucent accent tint
 * in the stylesheet uses — 73 of them, which were orchd's indigo written out
 * as `rgba(99, 102, 241, …)` until the panel became brandable.
 *
 * **The favicon link is re-pointed with a version**, although the server
 * renders `/favicon.svg` per instance: a browser that cached orchd's icon
 * would otherwise keep showing it.
 */
function dress(info: InstanceInfo): void {
  const { brand, name } = info;
  const root = document.documentElement.style;
  root.setProperty('--color-accent', brand.accent);
  root.setProperty('--color-accent-secondary', brand.accentSecondary);
  root.setProperty(
    '--accent-gradient',
    `linear-gradient(135deg, ${brand.accent}, ${brand.accentSecondary})`,
  );
  root.setProperty('--color-accent-rgb', rgbTriple(brand.accent));
  root.setProperty('--color-accent-secondary-rgb', rgbTriple(brand.accentSecondary));
  root.setProperty('--color-background', brand.background);

  document.title = brand.title ?? name;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', brand.background);

  const version = encodeURIComponent(`${brand.mark}-${brand.accent}-${brand.background}`);
  for (const [rel, path] of [
    ['icon', '/favicon.svg'],
    ['apple-touch-icon', '/apple-touch-icon.svg'],
  ] as const) {
    document.querySelector(`link[rel="${rel}"]`)?.setAttribute('href', `${path}?v=${version}`);
  }
}

export function InstanceProvider({ children }: { children: ReactNode }) {
  const [info, setInfo] = useState<InstanceInfo>({
    name: FALLBACK_INSTANCE,
    brand: DEFAULT_BRAND,
  });

  useEffect(() => {
    let live = true;
    void fetchInstance().then((i) => {
      if (!live) return;
      setInfo(i);
      dress(i);
    });
    return () => {
      live = false;
    };
  }, []);

  return (
    <InstanceContext.Provider value={info}>{children}</InstanceContext.Provider>
  );
}
